import "../Services.scss";

import { Col, Form, Row } from "react-bootstrap";
import { faAdd, faScissors, faSearch } from "@fortawesome/free-solid-svg-icons";
import { formatInReal, normalizeString } from "../../../commons/util/HallAppUtil";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppCard from "../../../commons/cards/AppCard";
import AppInputV2 from "../../../commons/inputs/AppInputV2";
import AppLoading from "../../../commons/AppLoading";
import CategoryAPI from "../CategoryAPI";
import CompanyAPI from "../../company/CompanyAPI";
import ConfirmModal from "../../../commons/modal/ConfirmModal";
import EmptyState from "../../../commons/EmptyState";
import FloatingButton from "../../../commons/buttons/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppCenterContainer from "../../../commons/containers/HallAppCenterContainer";
import HallAppToast from "../../../commons/HallAppToast";
import MenuTitle from "../../../commons/menu/MenuTitle";
import ModalServices from "./ModalServices";
import PullToRefresh from "react-simple-pull-to-refresh";
import ServiceAPI from "../ServiceAPI";
import noImg from "../../../assets/images/no-img.jpg";

const categoryAPI = new CategoryAPI();
const companyAPI = new CompanyAPI();
const serviceAPI = new ServiceAPI();

// Mapa de cores por categoria (persiste entre renders)
const categoryColorMap = {};

function generateLightColor() {
  const r = Math.floor(Math.random() * 128 + 128);
  const g = Math.floor(Math.random() * 128 + 128);
  const b = Math.floor(Math.random() * 128 + 128);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function getColorByCategory(categoryName) {
  if (!categoryColorMap[categoryName]) {
    categoryColorMap[categoryName] = generateLightColor();
  }
  return categoryColorMap[categoryName];
}

function Services() {
  const [showModalNewService, setShowModalNewService] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [serviceSelected, setServiceSelected] = useState(null);

  useEffect(() => {
    findAllServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findAllServices = async () => {
    try {
      setIsLoading(true);
      const response = await serviceAPI.findAllByCompanyId(companyAPI.getCompanyManagment().id);
      if (response.data) {
        response.data.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
        response.data.forEach((element) => {
          element.color = getColorByCategory(element.categoryName);
        });
      }
      setServices(response.data || []);
    } catch (_) {
      setServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredServices = useMemo(() => {
    const list = !searchTerm.trim()
      ? services
      : services.filter((service) => {
          const normalized = normalizeString(searchTerm);
          const normalizedName = normalizeString(service.name);
          const normalizedCategory = normalizeString(service.categoryName);
          return normalizedName.includes(normalized) || normalizedCategory.includes(normalized);
        });

    return [...list].sort((a, b) => {
      const c = (a.categoryName || "").localeCompare(b.categoryName || "", "pt-BR", { sensitivity: "base" });
      if (c !== 0) return c;
      return (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" });
    });
  }, [services, searchTerm]);

  async function executeDeletion() {
    if (!serviceSelected?.id) return;
    setIsLoadingModalDelete(true);
    try {
      await serviceAPI.deleteByIdAndCompanyId(serviceSelected.id);
      HallAppToast.success();
      setShowModalDelete(false);
      findAllServices();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  }

  async function showModalFormService() {
    setIsLoading(true);
    setCategories([]);
    try {
      const resp = await categoryAPI.findAllByCompanyId(companyAPI.getCompanyManagment().id);
      if (resp.data) {
        resp.data.sort((a, b) => a.name.localeCompare(b.name));
      }
      const options = resp.data.map((d) => ({ value: d.id, label: d.name }));
      setCategories(options);
    } catch (_) {
      setCategories([]);
    } finally {
      setIsLoading(false);
      setShowModalNewService(true);
    }
  }

  const hasSearch = !!searchTerm?.trim();

  return (
    <>
      {/* ✅ container só no desktop */}
      <div className="container-md services-page">
        <div className="services-header">
          <MenuTitle title="Serviços" showArrowInfo={false} />

          <div className="services-header__subtitle">
            Cadastre e organize seus serviços por categoria (ex: Corte, Escova, Maquiagem).
          </div>

          <div className="services-search">
            <Form>
              <AppInputV2
                name="search"
                label="Pesquisar"
                placeholder="Digite nome do serviço ou categoria..."
                errors={{}}
                touched={false}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<FontAwesomeIcon icon={faSearch} />}
                formText="Dica: use os botões Editar/Excluir no card."
              />
            </Form>

            {hasSearch && (
              <button type="button" className="services-clear" onClick={() => setSearchTerm("")}>
                Limpar
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <HallAppCenterContainer>
            <AppLoading />
          </HallAppCenterContainer>
        ) : (
          <PullToRefresh onRefresh={findAllServices}>
            <Row className="services-grid">
              {filteredServices.length === 0 ? (
                <Col xs={12}>
                  <EmptyState
                    title="Nenhum serviço encontrado"
                    description={
                      searchTerm
                        ? "Tente buscar por outro termo ou limpe o filtro."
                        : "Crie serviços como Corte, Escova e Maquiagem e associe a categorias para organizar sua agenda e relatórios."
                    }
                    actionLabel={searchTerm ? "Limpar busca" : "Cadastrar serviço"}
                    onAction={() => {
                      if (searchTerm) setSearchTerm("");
                      else showModalFormService();
                    }}
                    icon={<FontAwesomeIcon icon={faScissors} />}
                  />
                </Col>
              ) : (
                filteredServices.map((data) => (
                  <Col xs={12} sm={12} md={6} lg={4} key={data.id} className="mb-3">
                    <AppCard accent="brand">
                      <div className="servicev2-card">
                        {/* topo */}
                        <div className="servicev2-card__top">
                          <div
                            className="servicev2-card__badge"
                            style={{ backgroundColor: data?.color }}
                            title={data?.categoryName}
                          >
                            {data?.categoryName}
                          </div>
                        </div>

                        {/* corpo */}
                        <div className="servicev2-card__body">
                          <div className="servicev2-card__img">
                            <img src={data.logo || noImg} alt={data.name} />
                          </div>

                          <div className="servicev2-card__info">
                            <div className="servicev2-card__name">{data?.name}</div>

                            <div className="servicev2-card__meta">
                              <div className="servicev2-card__metaRow">
                                <span className="servicev2-card__metaLabel">Preço</span>
                                <span className="servicev2-card__metaValue">{formatInReal(data?.value)}</span>
                              </div>

                              <div className="servicev2-card__metaRow">
                                <span className="servicev2-card__metaLabel">Duração</span>
                                <span className="servicev2-card__metaValue">{data?.duration} min</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ✅ Ações iguais ao padrão de Categorias */}
                        <div className="service-miniCard__actions">
                          <AppButton
                            type="button"
                            width="100%"
                            name="Editar"
                            variant="secondary"
                            className="mb-0"
                            onClick={() => {
                              setServiceSelected(data);
                              showModalFormService();
                            }}
                          />

                          <AppButton
                            type="button"
                            width="100%"
                            name="Excluir"
                            variant="danger"
                            className="mb-0"
                            onClick={() => {
                              setServiceSelected(data);
                              setShowModalDelete(true);
                            }}
                          />
                        </div>
                      </div>
                    </AppCard>
                  </Col>
                ))
              )}
            </Row>
          </PullToRefresh>
        )}
      </div>

      {/* Modal de confirmação de exclusão */}
      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir serviço"
          type="danger"
          isLoading={isLoadingModalDelete}
          confirm={executeDeletion}
          closeModal={() => {
            setServiceSelected(null);
            setShowModalDelete(false);
          }}
          textBtnConfirm="Excluir"
          textBtnCancel="Cancelar"
        >
          Deseja remover o serviço <span style={{ fontWeight: "bold" }}>{serviceSelected?.name}</span> da categoria{" "}
          <span style={{ fontWeight: "bold" }}>{serviceSelected?.categoryName}</span>?
        </ConfirmModal>
      )}

      {/* FAB para criar */}
      <FloatingButton
        icon={faAdd}
        aria-label="Cadastrar serviço"
        onClick={async () => {
          setServiceSelected(null);
          showModalFormService();
        }}
      />

      {/* Modal de cadastro/edição */}
      {showModalNewService && (
        <ModalServices
          findAllServices={findAllServices}
          options={categories}
          show={showModalNewService}
          serviceId={serviceSelected?.id || null}
          closeShow={() => {
            setShowModalNewService(false);
            setServiceSelected(null);
          }}
        />
      )}
    </>
  );
}

export default Services;
